import { apiService } from '../../../components/api/apiService';
import { dataModelsCatalogService, type DataModelCatalogRow } from '../MCP/dataModelsCatalogService';
import { featureHelperService } from '../MCP/MCPResources/featureHelperService';
import { mcpConnectionService } from '../MCP/MCPResources/MCPResourcesV2/mcpConnectionService';
import type { McpServerConnection } from '../MCP/MCPResources/MCPResourcesV2/types';
import { slugify } from './governedMcpConstants';
import { logGovernedMcp } from './governedMcpLogger';
import type {
  DataModelField,
  DataModelObjectKind,
  DataModelSource,
  DataModelTable,
  ExternalMcpConnection,
  KnowledgeBaseSource,
  WorkflowSource,
} from './governedMcpTypes';
import { STANDARD_FILE_FIELDS, classifyBacking } from './publishSchemaBuilder';
import { toWorkflowSource, workflowListService } from './workflowListService';

export interface DataStoreRow {
  id: string;
  name: string;
  connectorKey: string;
}

function modelCode(name: string, id: string): string {
  const fromName = slugify(name).replace(/-/g, '_').toUpperCase();
  if (fromName) return fromName;
  return id.replace(/[^a-zA-Z0-9]/g, '').slice(-12).toUpperCase() || 'DATA_MODEL';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

export function extractMetadataRoot(metadataStructure?: string | object | null): Record<string, unknown> | null {
  if (metadataStructure == null) return null;
  let parsed: unknown = metadataStructure;
  if (typeof metadataStructure === 'string') {
    const trimmed = metadataStructure.trim();
    if (!trimmed) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      logGovernedMcp('warning', 'publish_metadata_parse_failed');
      return null;
    }
  }
  const rec = asRecord(parsed);
  if (!rec) return null;
  const nested = asRecord(rec.metadata);
  if (nested && (nested.catalog || nested.source)) return nested;
  return rec;
}

export function extractSourceMeta(root: Record<string, unknown> | null): { sourceType: string; category: string } {
  const source = asRecord(root?.source);
  return {
    sourceType: asString(source?.source_type ?? source?.sourceType),
    category: asString(source?.category),
  };
}

function mapField(raw: Record<string, unknown>): DataModelField | null {
  const name = asString(raw.name);
  if (!name) return null;
  return {
    name,
    displayName: asString(raw.display_name ?? raw.displayName) || name,
    dataType: asString(raw.data_type ?? raw.dataType ?? raw.type) || 'string',
    nullable: raw.nullable !== false && raw.nullable !== 'false',
    isPrimaryKey: Boolean(raw.is_primary_key ?? raw.isPrimaryKey),
  };
}

function mapFields(raw: unknown): DataModelField[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => (asRecord(item) ? mapField(item as Record<string, unknown>) : null))
    .filter((item): item is DataModelField => Boolean(item));
}

export function tablesFromMetadata(metadataStructure?: string | object | null): DataModelTable[] {
  const root = extractMetadataRoot(metadataStructure);
  if (!root) return [];
  const { sourceType, category } = extractSourceMeta(root);
  const backing = classifyBacking('', sourceType, category);
  const catalog = Array.isArray(root.catalog) ? root.catalog : [];
  const items: DataModelTable[] = [];
  const seen = new Set<string>();

  const push = (item: DataModelTable) => {
    if (!item.id || seen.has(item.id)) return;
    seen.add(item.id);
    items.push(item);
  };

  for (const entry of catalog) {
    const folder = asRecord(entry);
    if (!folder) continue;
    const parentType = asString(folder.parent_type ?? folder.parentType).toLowerCase();
    const folderId = asString(folder.id);
    const folderName = asString(folder.display_name ?? folder.displayName) || folderId;
    const isFolder = parentType === 'folder' || backing === 'google_drive';
    if (isFolder && folderId) {
      push({
        id: folderId,
        name: folderName,
        kind: 'folder',
        parentId: asString(folder.parent_id ?? folder.parentId) || undefined,
        fields: STANDARD_FILE_FIELDS,
      });
    }

    const entities = Array.isArray(folder.entities) ? folder.entities : [];
    for (const entity of entities) {
      const rec = asRecord(entity);
      if (!rec) continue;
      const id = asString(rec.id ?? rec.display_name ?? rec.displayName);
      if (!id) continue;
      const entityType = asString(rec.entity_type ?? rec.entityType).toLowerCase();
      const fields = mapFields(rec.fields);
      const kind: DataModelObjectKind =
        entityType === 'file' || entityType === 'folder' || backing === 'google_drive'
          ? entityType === 'folder'
            ? 'folder'
            : 'file'
          : 'table';
      push({
        id,
        name: asString(rec.display_name ?? rec.displayName) || id,
        kind,
        parentId: asString(rec.parent_id ?? rec.parentId) || folderId || undefined,
        fields: kind === 'file' && !fields.length ? STANDARD_FILE_FIELDS : fields,
      });
    }
  }

  logGovernedMcp('app', 'publish_catalog_flattened', {
    backing,
    objectCount: items.length,
    kinds: items.map((item) => item.kind),
    fieldCount: items.reduce((sum, item) => sum + item.fields.length, 0),
  });
  return items;
}

export function toDataModelSource(row: DataModelCatalogRow, store?: DataStoreRow): DataModelSource {
  const connectorKey = store?.connectorKey || row.type || '';
  return {
    id: row.id || row.uuid,
    name: row.modelName || row.id,
    code: modelCode(row.modelName, row.id || row.uuid),
    description: row.description || '',
    classification: 'internal',
    fields: [],
    status: row.status,
    tags: row.tags,
    datastoreId: row.type || store?.id,
    connectorKey,
    backing: classifyBacking(connectorKey),
  };
}

export function isActiveDataModel(model: DataModelSource): boolean {
  const status = (model.status || 'active').toLowerCase();
  return status === 'active';
}

export function isWorkflowReadyToPublish(workflow: WorkflowSource): boolean {
  const status = (workflow.status || '').toLowerCase();
  const activation = (workflow.activationStatus || 'active').toLowerCase();
  if (status === 'draft' || status === 'archived' || status === 'disabled') return false;
  if (activation === 'inactive' || activation === 'disabled' || activation === 'paused') return false;
  return true;
}

export const publishCatalogService = {
  async listDataStores(): Promise<DataStoreRow[]> {
    try {
      const data = await apiService.get('/api/DataStores', {}, 'backend');
      const list = data?.datastores ?? data?.dataStores ?? data?.data ?? [];
      if (!Array.isArray(list)) return [];
      return list.map((item: Record<string, unknown>) => ({
        id: String(item.id ?? item.Id ?? ''),
        name: String(item.name ?? item.Name ?? ''),
        connectorKey: String(item.connectorKey ?? item.ConnectorKey ?? item.type ?? ''),
      })).filter((item) => item.id);
    } catch (err) {
      logGovernedMcp('warning', 'publish_load_datastores_failed', {
        message: err instanceof Error ? err.message : String(err),
      });
      return [];
    }
  },

  async listDataModels(): Promise<DataModelSource[]> {
    logGovernedMcp('app', 'publish_load_data_models');
    try {
      const [rows, stores] = await Promise.all([dataModelsCatalogService.list(), publishCatalogService.listDataStores()]);
      const byId = new Map(stores.map((store) => [store.id, store]));
      const models = rows
        .map((row) => toDataModelSource(row, byId.get(row.type)))
        .filter((item) => item.id);
      logGovernedMcp('app', 'publish_load_data_models_ok', { count: models.length });
      return models;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data models';
      logGovernedMcp('error', 'publish_load_data_models_failed', { message });
      throw new Error(message);
    }
  },

  async getDataModelDetail(id: string): Promise<DataModelSource | null> {
    logGovernedMcp('app', 'publish_load_data_model_detail', { id });
    try {
      const raw = await apiService.get(`/api/DataModels/${id}`, {}, 'backend');
      const data =
        raw && typeof raw === 'object' && 'dataModel' in raw && raw.dataModel
          ? raw.dataModel
          : raw && typeof raw === 'object' && 'data' in raw && raw.data
            ? raw.data
            : raw;
      if (!data || typeof data !== 'object') return null;
      const rec = data as Record<string, unknown>;
      const stores = await publishCatalogService.listDataStores();
      const datastoreId = String(rec.type ?? rec.Type ?? '');
      const store = stores.find((item) => item.id === datastoreId);
      const metadata = rec.metadataStructure ?? rec.MetadataStructure;
      const root = extractMetadataRoot(metadata as string | object | null);
      const { sourceType, category } = extractSourceMeta(root);
      const tables = tablesFromMetadata(metadata as string | object | null);
      const fields = tables.flatMap((table) => table.fields.map((field) => field.name));
      const backing = classifyBacking(store?.connectorKey, sourceType, category);
      const model: DataModelSource = {
        id: String(rec.id ?? rec.Id ?? id),
        name: String(rec.modelName ?? rec.ModelName ?? id),
        code: modelCode(String(rec.modelName ?? rec.ModelName ?? ''), String(rec.id ?? id)),
        description: String(rec.description ?? rec.Description ?? ''),
        classification: 'internal',
        fields,
        status: String(rec.status ?? rec.Status ?? ''),
        tags: Array.isArray(rec.tags) ? rec.tags.map((tag) => String(tag)) : [],
        datastoreId,
        connectorKey: store?.connectorKey || sourceType || datastoreId,
        backing,
        tables,
        metadataStructure: typeof metadata === 'string' ? metadata : metadata ? JSON.stringify(metadata) : '',
      };
      logGovernedMcp('app', 'publish_load_data_model_detail_ok', {
        id: model.id,
        backing: model.backing,
        sourceType,
        category,
        tables: tables.length,
        fieldCount: fields.length,
        kinds: tables.map((item) => item.kind),
      });
      return model;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data model detail';
      logGovernedMcp('error', 'publish_load_data_model_detail_failed', { id, message });
      throw new Error(message);
    }
  },

  async createDataModel(payload: {
    entityName: string;
    description: string;
    sourceTypeUid: string;
    groupUuid: string;
    tags: string[];
    metadataStructure: string;
    additionalContext?: unknown;
    isActive: boolean;
  }): Promise<{ id: string; name: string }> {
    logGovernedMcp('app', 'publish_create_data_model', { name: payload.entityName });
    try {
      const raw = await apiService.post('/api/DataModels', payload, 'backend');
      const rec = asRecord((raw as { dataModel?: unknown })?.dataModel)
        || asRecord((raw as { data?: unknown })?.data)
        || asRecord(raw)
        || {};
      const id = asString(rec.id ?? rec.Id);
      const name = asString(rec.modelName ?? rec.ModelName ?? payload.entityName);
      if (!id) {
        logGovernedMcp('warning', 'publish_create_data_model_missing_id', { name });
      }
      logGovernedMcp('app', 'publish_create_data_model_ok', { id, name });
      return { id, name };
    } catch (err) {
      const axiosMessage =
        err && typeof err === 'object' && 'response' in err
          ? asString((err as { response?: { data?: { message?: unknown } } }).response?.data?.message)
          : '';
      const message = axiosMessage || (err instanceof Error ? err.message : 'Failed to create data model');
      logGovernedMcp('error', 'publish_create_data_model_failed', { message, name: payload.entityName });
      throw new Error(message);
    }
  },

  async listKnowledgeBases(): Promise<KnowledgeBaseSource[]> {
    logGovernedMcp('app', 'publish_load_knowledge_bases');
    try {
      const rows = await featureHelperService.listKnowledgeBases();
      const items = rows
        .map((row) => ({
          id: row.id,
          name: row.title || row.id,
          description: row.description || '',
          documentCount: row.documentCount,
        }))
        .filter((item) => item.id);
      logGovernedMcp('app', 'publish_load_knowledge_bases_ok', { count: items.length });
      return items;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load knowledge bases';
      logGovernedMcp('error', 'publish_load_knowledge_bases_failed', { message });
      throw new Error(message);
    }
  },

  async listWorkflows(): Promise<WorkflowSource[]> {
    const rows = await workflowListService.list();
    return rows.map((row) => toWorkflowSource(row));
  },

  async listExternalMcp(): Promise<ExternalMcpConnection[]> {
    logGovernedMcp('app', 'publish_load_mcp_connections');
    try {
      const rows = await mcpConnectionService.list();
      const connections = rows.map(toExternalMcpConnection).filter((item) => item.id);
      logGovernedMcp('app', 'publish_load_mcp_connections_ok', { count: connections.length });
      return connections;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load connected MCPs';
      logGovernedMcp('error', 'publish_load_mcp_connections_failed', { message });
      throw new Error(message);
    }
  },
};

export function toExternalMcpConnection(row: McpServerConnection): ExternalMcpConnection {
  return {
    id: row.id,
    name: row.name,
    code: row.code || row.id,
    description: row.description || '',
    publisher: row.source === 'catalog' ? 'Catalog' : 'Custom',
    tools: (row.tools || [])
      .filter((tool) => tool.enabled !== false)
      .map((tool) => ({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema ? JSON.stringify(tool.inputSchema, null, 2) : '',
      })),
    resources: (row.resources || [])
      .filter((resource) => resource.enabled !== false)
      .map((resource) => ({
        uri: resource.uri,
        name: resource.name,
        description: resource.description || '',
      })),
  };
}
